import { ReactNode } from "react";
import { LucideIcon, CheckCircle2, Circle, ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { Card, CardContent } from "./card";
import { Badge } from "./badge";
import { Button } from "./button";

interface ProcessStep {
  title: string;
  description: string;
  icon: LucideIcon;
  status?: "completed" | "current" | "upcoming";
  badge?: string;
  onClick?: () => void;
}

interface ProcessStepsGuideProps {
  steps: ProcessStep[];
  layout?: "grid" | "flow";
  className?: string;
}

/**
 * ProcessStepsGuide - Visual guide showing process steps
 * 
 * Features:
 * - Grid layout (default) for overview
 * - Flow layout for linear processes
 * - Status indicators (completed/current/upcoming)
 * - Icons and badges for visual clarity
 * 
 * Use in empty states or onboarding to show users the workflow.
 */
export function ProcessStepsGuide({
  steps,
  layout = "grid",
  className
}: ProcessStepsGuideProps) {
  if (layout === "flow") {
    return (
      <div className={cn("flex items-center gap-3 flex-wrap justify-center", className)}>
        {steps.map((step, index) => {
          const isLast = index === steps.length - 1;
          const Icon = step.icon;
          const status = step.status || "upcoming";

          return (
            <div key={index} className="flex items-center gap-3">
              <Card
                className={cn(
                  "relative flex-shrink-0 w-[200px] transition-all",
                  status === "completed" && "bg-primary/5 border-primary/20",
                  status === "current" && "bg-primary/10 border-primary ring-2 ring-primary/20"
                )}
              >
                {step.onClick && (
                  <Button type="button" variant="ghost" aria-label={step.title} onClick={step.onClick} className="absolute inset-0 z-10 h-full w-full rounded-lg focus-visible:ring-inset" />
                )}
                <CardContent className="p-4 space-y-2">
                  <div className="flex items-center gap-2">
                    {status === "completed" ? (
                      <CheckCircle2 className="h-5 w-5 text-primary" />
                    ) : status === "current" ? (
                      <Circle className="h-5 w-5 text-primary" fill="currentColor" />
                    ) : (
                      <Icon className="h-5 w-5 text-muted-foreground" />
                    )}
                    {step.badge && (
                      <Badge
                        className={cn(
                          status === "current" && "bg-primary text-primary-foreground"
                        )}
                      >
                        {step.badge}
                      </Badge>
                    )}
                  </div>
                  <h4 className="font-semibold text-sm">{step.title}</h4>
                  <p className="text-xs text-muted-foreground">
                    {step.description}
                  </p>
                </CardContent>
              </Card>

              {!isLast && (
                <ArrowRight className="h-6 w-6 text-muted-foreground flex-shrink-0" />
              )}
            </div>
          );
        })}
      </div>
    );
  }

  // Grid layout (default)
  return (
    <div
      className={cn(
        "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4",
        className
      )}
    >
      {steps.map((step, index) => {
        const Icon = step.icon;
        const status = step.status || "upcoming";

        return (
          <Card
            key={index}
            className={cn(
              "relative transition-all",
              status === "completed" && "bg-primary/5 border-primary/20",
              status === "current" && "bg-primary/10 border-primary ring-2 ring-primary/20"
            )}
          >
            {step.onClick && (
                  <Button type="button" variant="ghost" aria-label={step.title} onClick={step.onClick} className="absolute inset-0 z-10 h-full w-full rounded-lg focus-visible:ring-inset" />
                )}
                <CardContent className="p-4 space-y-2">
              <div className="flex items-center gap-2">
                {status === "completed" ? (
                  <CheckCircle2 className="h-6 w-6 text-primary" />
                ) : status === "current" ? (
                  <Circle className="h-6 w-6 text-primary" fill="currentColor" />
                ) : (
                  <Icon className="h-6 w-6 text-muted-foreground" />
                )}
                {step.badge && (
                  <Badge
                    className={cn(
                      status === "current" && "bg-primary text-primary-foreground"
                    )}
                  >
                    {step.badge}
                  </Badge>
                )}
              </div>
              <h4 className="font-semibold text-sm">{step.title}</h4>
              <p className="text-xs text-muted-foreground">
                {step.description}
              </p>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
